import { useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";

import styles from "./Field.module.css";

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  help?: string;
  error?: string | null;
  trailing?: ReactNode;
}

/** Labelled input with help text; the error replaces the help and is announced. */
export function Field({ label, help, error, trailing, className, ...input }: FieldProps) {
  const id = useId();
  const describedBy = `${id}-help`;
  return (
    <div className={[styles.field, className].filter(Boolean).join(" ")}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div className={styles.inputRow}>
        <input
          id={id}
          className={styles.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={help || error ? describedBy : undefined}
          {...input}
        />
        {trailing}
      </div>
      {(error ?? help) && (
        <p id={describedBy} className={error ? styles.error : styles.help} role={error ? "alert" : undefined}>
          {error ?? help}
        </p>
      )}
    </div>
  );
}

/** Form-level error (e.g. a failed login), announced to screen readers. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className={styles.formError} role="alert">
      {message}
    </p>
  );
}
